import { cameraLeft } from "./engine";
import { paintScenery, BIOME_BASE } from "./scenery";
import { paintEnemy, paintEnemyHealth, trackEnemies, burning, DANGER } from "./art/enemies";
import { paintBoss } from "./art/bosses";
import { paintBullet, bulletTrail } from "./art/projectiles";
import { paintBanner, paintEffects, paintPickups, paintWarnings } from "./art/effects";
import { FONT, INK, TAU, box, disc, glow, linear, makeCanvas, poly, radial, rgba, setGlowAdditive, shade } from "./art/kit";
import { icon } from "./art/icons";
import type { FlightDesign, FlightState, Pilot } from "./types";
import { renderTransparentDrawing } from "../../shared/art/drawing";
import type { Platform } from "../../client/platform";
import { BOSSES, SCENES } from "./content";
export { setArtCanvasFactory } from "./art/kit";

export interface Sprite {
  canvas: HTMLCanvasElement;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Lazily built dark silhouette used as the cast shadow. */
  shadow?: HTMLCanvasElement | null;
}
type Textures = Map<string, { ship: Sprite; bullet: Sprite; design: FlightDesign }>;
export function makeSprite(platform: Pick<Platform, "createCanvas">, drawing: FlightDesign["ship"]): Sprite {
  const canvas = platform.createCanvas(256, 256),
    c = canvas.getContext("2d")!;
  renderTransparentDrawing(c, drawing, 256);
  const data = c.getImageData(0, 0, 256, 256).data;
  let left = 256,
    top = 256,
    right = -1,
    bottom = -1;
  for (let y = 0; y < 256; y++)
    for (let x = 0; x < 256; x++)
      if (data[(y * 256 + x) * 4 + 3] > 15) {
        left = Math.min(left, x);
        right = Math.max(right, x);
        top = Math.min(top, y);
        bottom = Math.max(bottom, y);
      }
  return {
    canvas,
    x: left,
    y: top,
    w: Math.max(0, right - left + 1),
    h: Math.max(0, bottom - top + 1),
  };
}
export function drawSprite(c: CanvasRenderingContext2D, s: Sprite, x: number, y: number, size: number, angle = 0, source = s.canvas) {
  if (!s.w || !s.h) return;
  const scale = size / Math.max(s.w, s.h);
  c.save();
  c.translate(x, y);
  c.rotate(angle);
  c.drawImage(source, s.x, s.y, s.w, s.h, (-s.w * scale) / 2, (-s.h * scale) / 2, s.w * scale, s.h * scale);
  c.restore();
}
function shadowOf(s: Sprite) {
  if (s.shadow !== undefined) return s.shadow;
  const canvas = makeCanvas(256, 256);
  if (canvas) {
    const g = canvas.getContext("2d")!;
    g.drawImage(s.canvas, 0, 0);
    g.globalCompositeOperation = "source-in";
    g.fillStyle = "#0A1020";
    g.fillRect(0, 0, 256, 256);
  }
  return (s.shadow = canvas);
}

const ME = "#3FE0C5",
  MATE = "#FFB23E";
function exhaust(c: CanvasRenderingContext2D, x: number, y: number, size: number, tick: number, hot: boolean) {
  const flicker = 0.85 + 0.15 * Math.sin(tick * 1.3) + 0.08 * Math.sin(tick * 3.1),
    len = size * 0.55 * flicker * (hot ? 1.35 : 1),
    w = size * 0.13;
  glow(c, x, y + len * 0.3, w * 3.4, hot ? "#FF7A2E" : "#FFB23E", 0.8);
  poly(c, [[x - w, y], [x, y + len], [x + w, y]], hot ? "#FF5A2E" : "#FF9A3D", null);
  poly(c, [[x - w * 0.55, y], [x, y + len * 0.7], [x + w * 0.55, y]], "#FFE9A8", null);
  poly(c, [[x - w * 0.22, y], [x, y + len * 0.4], [x + w * 0.22, y]], "#FFFFFF", null);
}
function paintPilot(c: CanvasRenderingContext2D, s: FlightState, p: Pilot, textures: Textures, focus: boolean, shadow: number) {
  const t = textures.get(p.id),
    size = t?.design.shipSize ?? 92,
    tick = s.tick;
  if (p.hp <= 0) {
    if (t) {
      c.globalAlpha = 0.45;
      drawSprite(c, t.ship, p.x, p.y, size, 0.3);
      c.globalAlpha = 1;
    }
    const pulse = 0.5 + 0.5 * Math.sin(tick * 0.2);
    glow(c, p.x, p.y, 60 + pulse * 10, MATE, 0.5);
    c.strokeStyle = rgba("#FFFFFF", 0.35);
    c.lineWidth = 8;
    c.beginPath();
    c.arc(p.x, p.y, 50, 0, TAU);
    c.stroke();
    c.strokeStyle = "#5BE39A";
    c.lineCap = "round";
    c.beginPath();
    c.arc(p.x, p.y, 50, -Math.PI / 2, -Math.PI / 2 + (TAU * p.rescue) / 180);
    c.stroke();
    c.font = `900 20px ${FONT}`;
    c.textAlign = "center";
    c.textBaseline = "middle";
    box(c, p.x - 38, p.y + 58, 76, 30, 15, MATE, INK, 2.5);
    c.fillStyle = INK;
    c.fillText("靠近救援", p.x, p.y + 74);
    return;
  }
  const hot = (p.overdriveUntil ?? 0) > tick;
  // Escort drones from the wing side weapon (engine fires from x ± 60).
  if (p.levels.wing)
    for (const dx of [-60, 60]) {
      exhaust(c, p.x + dx, p.y + 2, 34, tick + dx, false);
      poly(c, [[p.x + dx, p.y - 26], [p.x + dx + 12, p.y + 4], [p.x + dx, p.y - 2], [p.x + dx - 12, p.y + 4]], linear(c, p.x + dx - 12, 0, p.x + dx + 12, 0, [[0, "#DFFBFF"], [1, "#4FB7D8"]]), INK, 2);
    }
  if (shadow > 0 && t) {
    const sh = shadowOf(t.ship);
    if (sh) {
      c.globalAlpha = shadow;
      drawSprite(c, t.ship, p.x + size * 0.28, p.y + size * 0.55, size * 0.86, 0, sh);
      c.globalAlpha = 1;
    }
  }
  exhaust(c, p.x, p.y + size * 0.38, size, tick, hot);
  if (hot) glow(c, p.x, p.y, size * 0.9, "#FF7A2E", 0.55);
  const blink = p.invulnerable > 0 && tick % 10 < 4;
  if (blink) c.globalAlpha = 0.45;
  if (t) drawSprite(c, t.ship, p.x, p.y, size);
  c.globalAlpha = 1;
  // Hit core: the only part enemy fire can hit, always clearly visible.
  glow(c, p.x, p.y, 16, focus ? ME : MATE, 0.9);
  disc(c, p.x, p.y, 7.5, INK);
  disc(c, p.x, p.y, 5.5, focus ? ME : MATE);
  disc(c, p.x, p.y, 2.8, "#FFFFFF");
  if (p.shield > 0) {
    const a = Math.min(1, p.shield / 40);
    c.save();
    c.fillStyle = radial(c, p.x, p.y, 30, 60, [
      [0, "rgba(120,220,255,0)"],
      [0.8, rgba("#78DCFF", 0.18 * a + 0.05)],
      [1, rgba("#BFF3FF", 0.5 * a + 0.2)],
    ]);
    c.beginPath();
    c.arc(p.x, p.y, 60, 0, TAU);
    c.fill();
    c.strokeStyle = rgba("#DFFBFF", 0.9);
    c.lineWidth = 3;
    c.beginPath();
    c.arc(p.x, p.y, 60, tick * 0.05, tick * 0.05 + 1.2);
    c.stroke();
    c.beginPath();
    c.arc(p.x, p.y, 60, tick * 0.05 + Math.PI, tick * 0.05 + Math.PI + 0.7);
    c.stroke();
    c.restore();
  }
  // Orbit satellites match the engine: n + 1 drones, radius 85, 0.038 rad/tick.
  const n = p.levels.orbit ?? 0;
  if (n)
    for (let i = 0; i < n + 1; i++) {
      const a = tick * 0.038 + (i * TAU) / (n + 1),
        x = p.x + Math.cos(a) * 85,
        y = p.y + Math.sin(a) * 85;
      c.strokeStyle = rgba("#9FF4FF", 0.35);
      c.lineWidth = 6;
      c.beginPath();
      c.arc(p.x, p.y, 85, a - 0.5, a);
      c.stroke();
      glow(c, x, y, 20, "#7FE9FF", 0.8);
      disc(c, x, y, 10, INK);
      disc(c, x, y, 8, radial(c, x - 2, y - 2, 0, 9, [[0, "#FFFFFF"], [0.5, "#9FF4FF"], [1, "#2F9FD0"]]));
    }
  if (!focus) {
    c.strokeStyle = rgba(MATE, 0.9);
    c.lineWidth = 3;
    c.setLineDash([6, 6]);
    c.beginPath();
    c.arc(p.x, p.y, size * 0.62, 0, TAU);
    c.stroke();
    c.setLineDash([]);
  }
}

function bossWarning(c: CanvasRenderingContext2D, s: FlightState) {
  const left = s.nextBoss - s.tick;
  if (!(left < 180 && left > 0)) return;
  const pulse = 0.5 + 0.5 * Math.sin(s.tick * 0.35);
  c.save();
  c.fillStyle = rgba(DANGER, 0.12 + pulse * 0.12);
  c.fillRect(0, 0, 720, 1280);
  c.translate(360, 420);
  c.fillStyle = "rgba(20,10,20,0.82)";
  c.fillRect(-360, -70, 720, 140);
  for (const y of [-70, 58]) {
    c.save();
    c.beginPath();
    c.rect(-360, y, 720, 12);
    c.clip();
    for (let x = -400 + ((s.tick * 3) % 40); x < 400; x += 40) poly(c, [[x, y + 12], [x + 20, y], [x + 32, y], [x + 12, y + 12]], "#FFC94D", null);
    c.restore();
  }
  c.font = `900 22px ${FONT}`;
  c.textAlign = "center";
  c.textBaseline = "middle";
  c.fillStyle = rgba("#FF7A8A", 0.7 + pulse * 0.3);
  c.fillText("— 强 敌 接 近 —", 0, -34);
  c.font = `900 48px ${FONT}`;
  c.lineJoin = "round";
  c.strokeStyle = DANGER;
  c.lineWidth = 8;
  const name = BOSSES[s.bosses % (s.version === 2 ? 6 : 3)];
  c.strokeText(name, 0, 18);
  c.fillStyle = "#FFF3E0";
  c.fillText(name, 0, 18);
  c.restore();
}

function teammateArrows(c: CanvasRenderingContext2D, s: FlightState, focusId: string | undefined, left: number) {
  for (const p of s.pilots) {
    if (p.id === focusId || p.left) continue;
    const x = p.x - left;
    if (x >= 25 && x <= 695) continue;
    const side = x < 25 ? -1 : 1,
      ax = side < 0 ? 26 : 694,
      ay = Math.max(170, Math.min(1130, p.y));
    c.save();
    c.translate(ax, ay);
    glow(c, 0, 0, 34, MATE, 0.6);
    disc(c, 0, 0, 22, MATE, INK, 3);
    poly(c, [[side * 30, 0], [side * 18, -9], [side * 18, 9]], MATE, INK, 2.5);
    icon(c, p.hp > 0 ? "team" : "plus", 0, 0, 22, "#FFFFFF");
    c.restore();
  }
}

/**
 * Paints the battle into a width×height box (letterboxed 720×1280 logical
 * field). `bleed` extends the scenery upward by that many pixels, e.g. under
 * the status bar. Returns the logical→screen mapping and a flash strength.
 */
export function paintFlight(
  c: CanvasRenderingContext2D,
  s: FlightState,
  textures: Textures,
  width: number,
  height: number,
  offsetY = 0,
  focusId?: string,
  bleed = 0,
) {
  const scale = Math.min(width / 720, height / 1280),
    ox = (width - 720 * scale) / 2,
    oy = offsetY + (height - 1280 * scale) / 2,
    biome = ((s.scene % 6) + 6) % 6,
    base = BIOME_BASE[biome];
  // Side letterboxes (tablets): a dark frame in the biome's hue.
  c.fillStyle = linear(c, 0, offsetY - bleed, 0, offsetY + height, [
    [0, shade(base, -0.55)],
    [1, shade(base, -0.7)],
  ]);
  // The bottom safe-area strip is covered too; painting off-canvas is harmless.
  const below = bleed ? 80 : 0;
  c.fillRect(0, offsetY - bleed, width, height + bleed + below);
  c.save();
  c.translate(ox, oy);
  c.scale(scale, scale);
  const top = (offsetY - bleed - oy) / scale,
    bottom = (offsetY + height + below - oy) / scale;
  c.beginPath();
  c.rect(0, top, 720, bottom - top);
  c.clip();
  setGlowAdditive(biome === 2);
  const left = cameraLeft(s, focusId),
    focus = s.pilots.find((p) => p.id === focusId) ?? s.pilots[0],
    shadow = biome === 2 ? 0.12 : 0.28;
  paintScenery(c, s.scene, s.tick, focus ?? { x: 360, y: 1000 }, left, top, bottom);
  c.translate(-left, 0);
  trackEnemies(s.enemies, s.tick);
  for (const e of s.enemies) if (e.altitude === "ground" && !e.boss) paintEnemy(c, e, s.tick, shadow);
  paintWarnings(c, s);
  for (const e of s.enemies) if (e.boss) paintBoss(c, e, s.tick, shadow);
  for (const e of s.enemies) if (e.altitude !== "ground" && !e.boss) paintEnemy(c, e, s.tick, shadow);
  for (const e of s.enemies) burning(c, e, s.tick);
  for (const e of s.enemies) paintEnemyHealth(c, e);
  paintPickups(c, s);
  for (const b of s.bullets) {
    if (b.owner === "enemy" || b.life <= 0) continue;
    if (paintBullet(c, b, s.tick)) continue;
    bulletTrail(c, b);
    const t = textures.get(b.owner);
    if (t) drawSprite(c, t.bullet, b.x, b.y, t.design.bulletSize * (b.radius / 7), Math.atan2(b.vy, b.vx) + Math.PI / 2);
    else disc(c, b.x, b.y, b.radius, "#6FD6FF", INK, 2);
  }
  for (const p of s.pilots) if (!p.left) paintPilot(c, s, p, textures, p.id === focus?.id, shadow);
  const flash = paintEffects(c, s);
  for (const b of s.bullets) if (b.owner === "enemy" && b.life > 0) paintBullet(c, b, s.tick);
  c.translate(left, 0);
  if (s.version === 2) teammateArrows(c, s, focus?.id, left);
  paintBanner(c, s);
  bossWarning(c, s);
  c.restore();
  if (flash > 0) {
    c.fillStyle = `rgba(255,250,235,${flash * 0.8})`;
    c.fillRect(0, offsetY - bleed, width, height + bleed);
  }
  return { x: ox, y: oy, scale, left, scene: SCENES[s.scene % SCENES.length], flash };
}
